import React, { useState } from "react";
import "./ProteinPowderComparison.css"; // Assuming you have a CSS file for styling

const ProteinPowderComparison = () => {
  const [selectedProtein, setSelectedProtein] = useState("whey");

  // Function to handle selection of protein type
  const handleProteinSelection = (proteinType) => {
    setSelectedProtein(proteinType);
  };

  return (
    <div className="protein-powder-comparison-container">
      <h3 className="section-title">2.6. Protein Powder Comparisons</h3>
      <p className="intro-text">
        When choosing a protein powder, consider factors like protein content, digestibility, and price. Here's a detailed
        comparison of popular protein powder types:
      </p>

      <div className="protein-options">
        <button
          className={`protein-powder-button ${selectedProtein === "whey" ? "active" : ""}`}
          onClick={() => handleProteinSelection("whey")}
        >
          Whey Protein
        </button>
        <button
          className={`protein-powder-button ${selectedProtein === "casein" ? "active" : ""}`}
          onClick={() => handleProteinSelection("casein")}
        >
          Casein Protein
        </button>
        <button
          className={`protein-powder-button ${selectedProtein === "plant" ? "active" : ""}`}
          onClick={() => handleProteinSelection("plant")}
        >
          Plant-Based Protein
        </button>
        <button
          className={`protein-powder-button ${selectedProtein === "lactoseFree" ? "active" : ""}`}
          onClick={() => handleProteinSelection("lactoseFree")}
        >
          Lactose-Free Options
        </button>
      </div>

      <div className="protein-details">
        {selectedProtein === "whey" && (
          <div className="protein-info">
            <h4 className="protein-title">Whey Protein</h4>
            <p>
              Whey protein is a fast-digesting protein, ideal for post-workout recovery. It is rich in branched-chain amino
              acids (BCAAs), which are essential for muscle repair. Whey provides around 20-25g of protein per serving and
              is typically absorbed quickly by the body.
            </p>
            <ul>
              <li>
                <strong>Advantages:</strong> Fast digestion, supports muscle growth and recovery, contains BCAAs.
              </li>
              <li>
                <strong>Disadvantages:</strong> May cause digestive discomfort for some individuals, not suitable for those with
                lactose intolerance.
              </li>
            </ul>
          </div>
        )}

        {selectedProtein === "casein" && (
          <div className="protein-info">
            <h4 className="protein-title">Casein Protein</h4>
            <p>
              Casein is a slow-digesting protein, perfect for providing a sustained release of amino acids. It is commonly
              consumed before bedtime to aid in muscle recovery during sleep. It provides around 20g of protein per serving.
            </p>
            <ul>
              <li>
                <strong>Advantages:</strong> Slow digestion, ideal for overnight recovery, sustained amino acid release.
              </li>
              <li>
                <strong>Disadvantages:</strong> May not be suitable for post-workout, slower absorption compared to whey.
              </li>
            </ul>
          </div>
        )}

        {selectedProtein === "plant" && (
          <div className="protein-info">
            <h4 className="protein-title">Plant-Based Protein</h4>
            <p>
              Plant-based protein powders are derived from various plant sources like peas, hemp, and rice. They are a great
              option for vegans and vegetarians and are typically lower in BCAAs compared to whey but still offer solid protein
              content. Plant proteins typically provide 15-20g of protein per serving.
            </p>
            <ul>
              <li>
                <strong>Advantages:</strong> Suitable for vegans and vegetarians, typically lactose-free, rich in fiber.
              </li>
              <li>
                <strong>Disadvantages:</strong> May have lower BCAA content, sometimes less digestible for some individuals.
              </li>
            </ul>
          </div>
        )}

        {selectedProtein === "lactoseFree" && (
          <div className="protein-info">
            <h4 className="protein-title">Lactose-Free Options</h4>
            <p>
              If you are lactose intolerant, there are various lactose-free protein powder options available. These include
              whey isolate, egg white protein, and plant-based proteins, all of which are gentle on the stomach.
            </p>
            <ul>
              <li>
                <strong>Whey Isolate:</strong> A form of whey protein that has been processed to remove most of the lactose.
              </li>
              <li>
                <strong>Plant-Based Proteins:</strong> Naturally lactose-free, perfect for those avoiding dairy.
              </li>
              <li>
                <strong>Egg White Protein:</strong> High-quality, lactose-free protein, rich in essential amino acids.
              </li>
            </ul>
          </div>
        )}
      </div>

      <div className="research-section">
        <h4 className="section-title">Recent Research and Studies on Protein Intake</h4>
        <p>
          Multiple studies have shown that protein intake plays a crucial role in muscle growth, recovery, and weight loss. A
          study published in the <strong>Journal of Sports Science and Medicine</strong> concluded that a high-protein diet
          supports muscle mass retention during weight loss and aids in faster muscle repair post-workout. Additionally, a
          recent study in <strong>Nutrition Reviews</strong> highlighted the importance of protein timing—consuming protein
          immediately post-workout can maximize muscle recovery.
        </p>
      </div>

      <div className="benefit-section">
        <h4 className="section-title">Benefits of Protein Powder</h4>
        <ul>
          <li><strong>Supports Muscle Growth:</strong> Protein powders provide a convenient way to increase muscle mass.</li>
          <li><strong>Aids in Weight Loss:</strong> Protein can help you feel fuller for longer, reducing overall calorie intake.</li>
          <li><strong>Improves Recovery:</strong> Consuming protein after exercise accelerates muscle repair.</li>
        </ul>
      </div>

      <div className="side-effects-section">
        <h4 className="section-title">Potential Side Effects of Protein Powder</h4>
        <p>
          While protein powders can be incredibly beneficial, they can also come with some side effects, including digestive
          discomfort, kidney strain (if consumed excessively), and potential allergic reactions (especially with whey and
          dairy-based proteins). It’s important to consume protein in moderation and choose the type that suits your body’s
          needs.
        </p>
      </div>

      <div className="testimonial-section">
        <h4 className="section-title">User Testimonials</h4>
        <div className="testimonial">
          <p>
            "I’ve been using whey protein for over a year, and I’ve seen significant improvements in my muscle growth and
            recovery times after workouts. It’s my go-to post-workout shake!" – <strong>John D.</strong>
          </p>
        </div>
        <div className="testimonial">
          <p>
            "As a vegan, I’ve been searching for a plant-based protein powder that actually tastes good. I found one that has
            20g of protein per serving and a great flavor—perfect for my needs!" – <strong>Sarah L.</strong>
          </p>
        </div>
      </div>

      <div className="comparison-table">
        <h4 className="section-title">Protein Powder Comparison Table</h4>
        <table>
          <thead>
            <tr>
              <th>Protein Type</th>
              <th>Protein Content</th>
              <th>Digestibility</th>
              <th>Best For</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Whey</td>
              <td>20-25g per serving</td>
              <td>Fast</td>
              <td>Post-workout recovery</td>
            </tr>
            <tr>
              <td>Casein</td>
              <td>20g per serving</td>
              <td>Slow</td>
              <td>Overnight recovery</td>
            </tr>
            <tr>
              <td>Plant-Based</td>
              <td>15-20g per serving</td>
              <td>Moderate</td>
              <td>Vegans, vegetarians</td>
            </tr>
            <tr>
              <td>Lactose-Free</td>
              <td>Varies</td>
              <td>Varies</td>
              <td>Lactose-intolerant</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProteinPowderComparison;
